import type { SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import { tokens } from '@hotjar/hotsauce';

import { Icon } from '@hotjar/webapp/components/Icon';
import { CircleIcon } from '@hotjar/webapp/components/CircleIcon';

const iconSpacing = css`
  margin-right: 14px;
  margin-top: 1px;
`;

interface MessageProps {
  color: string;
  css?: SerializedStyles | SerializedStyles[] | undefined;
  icon: string;
  circleIcon?: boolean;
  iconColor: string;
  iconBackgroundColor?: string;
  iconStyles?: SerializedStyles | SerializedStyles[];
  onCloseClick?: (event: React.SyntheticEvent<HTMLDivElement, MouseEvent>) => void;
  children: React.ReactNode;
}

export const Message = ({
  color,
  icon,
  circleIcon,
  iconColor,
  iconBackgroundColor,
  iconStyles,
  onCloseClick,
  children,
  ...props
}: MessageProps): JSX.Element => (
  <div
    css={css`
      color: ${color};
      display: flex;
      align-items: flex-start;
      word-break: break-word;
      margin-bottom: 12px;
      a {
        cursor: pointer;
        color: inherit;
        text-decoration: underline;
      }
    `}
    {...props}
  >
    {circleIcon ? (
      <CircleIcon
        css={[iconSpacing, iconStyles as SerializedStyles]}
        size={16}
        icon={icon}
        backgroundColor={iconBackgroundColor as string}
        color={iconColor}
      />
    ) : (
      <Icon css={[iconSpacing, iconStyles]} size={16} icon={icon} color={iconColor} />
    )}
    <div
      css={css`
        flex: 1;
      `}
    >
      {children}
    </div>
    {/* FIXME: jsx-a11y/click-events-have-key-events */}
    {/* eslint-disable jsx-a11y/click-events-have-key-events */}
    {onCloseClick && (
      <div
        role="button"
        tabIndex={0}
        data-testid="close-icon"
        css={css`
          align-self: center;
          cursor: pointer;
        `}
        onClick={onCloseClick}
      >
        <Icon size={16} icon="x" color={tokens.color.white} />
      </div>
    )}
    {/* eslint-enable jsx-a11y/click-events-have-key-events */}
  </div>
);

export const InfoMessage = ({ children, ...props }: Partial<MessageProps>): JSX.Element => (
  <Message
    color={tokens.color.text_light}
    iconBackgroundColor={tokens.color.ui_background}
    iconColor={tokens.color.text_light}
    icon="info"
    {...props}
  >
    {children}
  </Message>
);

export const SuccessMessage = ({ children, ...props }: Partial<MessageProps>): JSX.Element => (
  <Message
    color={tokens.color.mint_600}
    circleIcon
    iconBackgroundColor={tokens.color.mint_600}
    iconColor={tokens.color.white}
    icon="check"
    {...props}
  >
    {children}
  </Message>
);

export const SuccessMessageFull = ({ children, ...props }: Partial<MessageProps>): JSX.Element => (
  <SuccessMessage
    circleIcon={false}
    color={tokens.color.white}
    css={css`
      background-color: ${tokens.color.mint_600};
      border-radius: 2px;
      padding: 8px 20px 8px 14px;
    `}
    {...props}
  >
    {children}
  </SuccessMessage>
);

export const HelpMessage = ({ children, ...props }: Partial<MessageProps>): JSX.Element => (
  <Message
    iconStyles={css`
      margin-right: 8px;
    `}
    color={tokens.color.text_default}
    iconColor={tokens.color.text_default}
    icon="help-on"
    {...props}
  >
    {children}
  </Message>
);

export const ErrorMessage = ({ children, ...props }: Partial<MessageProps>): JSX.Element => (
  <Message
    color={tokens.color.white}
    iconColor={tokens.color.white}
    icon="info"
    css={css`
      background-color: ${tokens.color.magenta_600};
      border-radius: 2px;
      padding: 8px 20px 8px 14px;
    `}
    iconStyles={css`
      transform: rotate(180deg);
    `}
    {...props}
  >
    {children}
  </Message>
);

export const NotificationMessage = ({ children, ...props }: Partial<MessageProps>): JSX.Element => (
  <Message
    color={tokens.color.white}
    iconColor={tokens.color.white}
    icon="info"
    css={css`
      background-color: ${tokens.color.neutral_800};
      border-radius: 2px;
      padding: 8px 20px 8px 14px;
    `}
    iconStyles={css`
      transform: rotate(180deg);
    `}
    {...props}
  >
    {children}
  </Message>
);

export const InvertedNotificationMessage = ({
  children,
  ...props
}: Partial<MessageProps>): JSX.Element => (
  <Message
    color={tokens.color.neutral_600}
    iconColor={tokens.color.neutral_600}
    icon="info"
    css={css`
      background-color: ${tokens.color.bg_active};
      border-radius: 2px;
      padding: 8px 20px 8px 14px;
    `}
    iconStyles={css`
      transform: rotate(180deg);
    `}
    {...props}
  >
    {children}
  </Message>
);

export const WarningMessage = ({ children, ...props }: Partial<MessageProps>): JSX.Element => {
  return (
    <Message
      color={tokens.color.white}
      iconColor={tokens.color.white}
      icon="info"
      css={css`
        background-color: ${tokens.color.ember_600};
        border-radius: 2px;
        padding: 8px 20px 8px 14px;
      `}
      iconStyles={css`
        transform: rotate(180deg);
      `}
      {...props}
    >
      {children}
    </Message>
  );
};
